import { LitElement, html } from 'lit';
import { store } from '../store.js';
import { getWeekStart, getWeekLabel, getDaysOfWeek, getDayLabel } from '../utils/date.js';
import { formatCookTime } from '../utils/format.js';
import { confirmAction } from '../components/confirm-dialog.js';
import { showLoading, hideLoading } from '../components/loading-spinner.js';
import { showSuccess, showError } from '../components/toast-notification.js';

class PlannerView extends LitElement {
  static properties = {
    _weekStart: { state: true },
    _isUpdating: { state: true },
  };

  createRenderRoot() { return this; }

  constructor() {
    super();
    this._weekStart = getWeekStart();
    this._isUpdating = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._onUpdate = () => this.requestUpdate();
    store.on('mealplan-updated', this._onUpdate);
    store.on('recipes-updated', this._onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    store.off('mealplan-updated', this._onUpdate);
    store.off('recipes-updated', this._onUpdate);
  }

  async _onRecipeSelect(e, day) {
    if (this._isUpdating) return;
    const recipeId = e.target.value;

    try {
      this._isUpdating = true;
      showLoading();
      if (recipeId) {
        await store.updateMealInPlan(this._weekStart, day, { recipe_id: recipeId });
        showSuccess('Meal updated');
      } else {
        await store.clearMealFromPlan(this._weekStart, day);
        showSuccess('Meal cleared');
      }
    } catch (error) {
      console.error('Error updating meal plan:', error);
      showError('Failed to update meal plan');
    } finally {
      this._isUpdating = false;
      hideLoading();
    }
  }

  async _clearDay(day) {
    if (this._isUpdating) return;
    try {
      this._isUpdating = true;
      showLoading();
      await store.clearMealFromPlan(this._weekStart, day);
      showSuccess('Meal cleared');
    } catch (error) {
      console.error('Error clearing meal:', error);
      showError('Failed to clear meal');
    } finally {
      this._isUpdating = false;
      hideLoading();
    }
  }

  async _generateShopping() {
    if (this._isUpdating) return;
    try {
      this._isUpdating = true;
      showLoading();
      await store.generateShoppingList(this._weekStart);
      showSuccess('Shopping list generated');
      window.location.hash = '#/shopping';
    } catch (error) {
      console.error('Error generating shopping list:', error);
      showError(error.message || 'Failed to generate shopping list');
    } finally {
      this._isUpdating = false;
      hideLoading();
    }
  }

  async _clearWeek() {
    if (this._isUpdating) return;
    const confirmed = await confirmAction('Are you sure you want to clear all meals for this week?');
    if (!confirmed) return;

    try {
      this._isUpdating = true;
      showLoading();
      await store.clearWeek(this._weekStart);
      showSuccess('Week cleared');
    } catch (error) {
      console.error('Error clearing week:', error);
      showError('Failed to clear week');
    } finally {
      this._isUpdating = false;
      hideLoading();
    }
  }

  render() {
    const weekLabel = getWeekLabel(this._weekStart);
    const meals = store.getMealPlanForWeek(this._weekStart);
    const recipes = store.getRecipes();
    const days = getDaysOfWeek();

    const mealsByDay = {};
    meals.forEach(m => { mealsByDay[m.day] = m; });

    return html`
      <div class="max-w-7xl mx-auto px-4 py-6 md:ml-64">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">Meal Planner</h1>
            <p class="text-gray-600 mt-1">${weekLabel}</p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow mb-6">
          <div class="divide-y divide-gray-200">
            ${days.map(day => {
              const meal = mealsByDay[day];
              const recipe = meal?.recipe_id ? store.getRecipe(meal.recipe_id) : null;
              return html`
                <div class="p-6">
                  <div class="flex flex-col md:flex-row md:items-center gap-4">
                    <div class="md:w-32">
                      <span class="font-semibold text-gray-700">${getDayLabel(day)}</span>
                    </div>
                    <div class="flex-1">
                      <select class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" @change=${(e) => this._onRecipeSelect(e, day)}>
                        <option value="">Select a recipe...</option>
                        ${recipes.map(r => html`
                          <option value=${r.id} ?selected=${recipe?.id === r.id}>
                            ${r.name} ${r.cook_time_mins ? `(${r.cook_time_mins} min)` : ''}
                          </option>
                        `)}
                      </select>
                    </div>
                    ${recipe ? html`
                      <div class="flex items-center gap-2">
                        ${recipe.cook_time_mins ? html`<span class="text-sm text-gray-600">${formatCookTime(recipe.cook_time_mins)}</span>` : ''}
                        <button @click=${() => this._clearDay(day)} class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                      </div>
                    ` : ''}
                  </div>
                </div>
              `;
            })}
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <button @click=${this._generateShopping} class="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors">
            Generate Shopping List
          </button>
          <button @click=${this._clearWeek} class="px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
            Clear Week
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('planner-view', PlannerView);
